import type { ClientBanner } from '../api/types.ts';
import { LesserFlags, ResolvedSafetyFlags } from './FlagBanner.tsx';

/**
 * The client header.
 *
 * The name is deliberately *below* the Critical band. That will feel wrong for
 * about a day. The header exists because warnings were being written into name
 * fields; putting the warning back under the name recreates the problem with
 * better typography.
 *
 * The whole block is sticky, so the band stays put while the history scrolls.
 * Glancing at a tablet mid-conversation is exactly when someone scrolls a
 * warning off the screen.
 */
/**
 * The Critical band is rendered by the *page*, not here.
 *
 * On a wide screen the header sits in a column beside the timeline, and a
 * Critical band confined to that column is no longer full-bleed — which is the
 * one thing about this screen that is not negotiable. So the page renders the
 * band across the full width above everything, at every size, and this
 * component renders the rest. One placement, no breakpoint branching, and no
 * chance of the rule quietly weakening at a width nobody checked.
 */
export function splitByBand(banner: ClientBanner) {
  const flags = banner.flags ?? [];
  return {
    // band, not severity. Health and Safety are Critical whatever was typed —
    // gms.flag_band() decides, so the rule is the same for every caller rather
    // than one a component happens to apply.
    critical: flags.filter((f) => f.band === 'Critical'),
    lesser: flags.filter((f) => f.band !== 'Critical'),
  };
}

export function ClientHeader({ banner }: { banner: ClientBanner }) {
  const { lesser } = splitByBand(banner);
  const resolvedSafety = banner.resolved_safety_flags ?? [];

  return (
    <header className="bg-white shadow-sm lg:rounded-lg lg:shadow">
      <div className="px-4 pt-3.5 pb-3">
        <div className="flex items-baseline gap-3">
          <h1 className="min-w-0 text-[1.375rem] leading-tight font-bold text-neutral-900">
            {banner.display_name}
          </h1>
          <span className="ml-auto shrink-0 font-mono text-base text-neutral-500 tabular-nums">
            {banner.client_ref}
          </span>
        </div>

        <p className="mt-1.5 text-[0.9375rem] leading-snug text-neutral-600">
          {/* Deduplicated. `status` is the four-word tracker vocabulary (Active,
              Deceased, Discharged, No visits needed) and `legal_status` is the
              legal one (AIP, IP, Deceased, Discharged); for a living client
              they differ and both are worth saying — "Active · IP". For a
              client who has died or been discharged they are the same word, and
              the header read "Deceased · Deceased". */}
          {[...new Set([banner.status, banner.legal_status, banner.appointment])]
            .filter(Boolean)
            .join(' · ')}
        </p>

        <dl className="mt-3 space-y-1.5 text-[0.9375rem]">
          <div className="flex gap-2">
            <dt className="w-28 shrink-0 text-neutral-500">Where</dt>
            <dd className="min-w-0 font-medium text-neutral-800">
              {banner.whereabouts_unknown ? (
                <span className="text-critical-edge font-bold">
                  Whereabouts unknown
                  {banner.where_to_find_them && (
                    <span className="block font-medium text-neutral-700">
                      Last known: {banner.where_to_find_them}
                    </span>
                  )}
                </span>
              ) : (
                // The street, not the home's derived name (061), as on the facility
                // board: the header is read on shared screens too, and the rule is
                // that a place is shown by where it is, not by who lives there
                // (Arthur, 23 September). See PLACE_NAME in current-place.ts.
                banner.where_to_find_them ?? 'Not recorded'
              )}
            </dd>
          </div>

          <div className="flex gap-2">
            <dt className="w-28 shrink-0 text-neutral-500">Visits</dt>
            <dd className="min-w-0 text-neutral-800">
              {banner.visits_required === null ? (
                'Not set'
              ) : banner.visits_required === 0 ? (
                /* A BARE ZERO DOES NOT SAY WHY (040). There are two routes to
                   nought visits — the client is visit-exempt, or there is no
                   live appointment so no obligation holds — and only
                   `visit_basis` tells them apart. This read "Exempt" for both,
                   which is a clinical claim about a client the agency may
                   simply not be appointed for yet. */
                <span className="font-medium">{banner.visit_basis ?? 'None owed'}</span>
              ) : (
                <>
                  <span className="font-medium">{banner.visits_required} a year</span>
                  {banner.visit_basis && (
                    <span className="text-neutral-500"> · {banner.visit_basis}</span>
                  )}
                </>
              )}
            </dd>
          </div>
        </dl>
      </div>

      <LesserFlags flags={lesser} />
      <ResolvedSafetyFlags flags={resolvedSafety} />
    </header>
  );
}
